import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import moment from "moment";
import {compose} from "mu-lambda";
import {useContext, useEffect, useMemo, useState} from "react";
import {Helmet} from "react-helmet-async";

import {useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    enums,
    usePublishAnalyticsEvent,
    withConversionContext,
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import {
    getSelectedDateRange,
    type DateRangeSelection,
    type DateRangeValue,
} from "@khan/component-date-range-dropdown";
import {useQuery} from "@khan/data-gql";
import {AppShellContext} from "@khan/shared-core/context";
import {useLocation} from "@khan/urls";

import type {ColumnName} from "../column-names.ts";
import {ConversationsViewHeading} from "../conversation-view-heading.tsx";

import type {guideTranscripts_aiGuideThreads_threads as AIGuideThreads} from "./__graphql-types__/guideTranscripts.ts";
import {GuideTranscriptsContent} from "./guide-transcripts-content.tsx";
import {GuideTranscriptsFilters} from "./guide-transcripts-filters.tsx";
import {GUIDE_TRANSCRIPTS_QUERY} from "./guide-transcripts-query.ts";

// Constants
const DEFAULT_DATE_RANGE_VALUE: DateRangeValue = "last-30-days";
const GUIDE_TRANSCRIPTS_QUERY_PAGE_SIZE = 50;
const TABLE_PAGE_SIZE = 10;
const CLASSROOM_ENTRYPOINT = "classroom";
const TUTOR_ME_PATH = "/tutor-me";

// Types
type Relationship = "SELF" | "COACH" | "DISTRICT_ADMIN" | "OTHER";

interface OwnProps {
    /**
     * The kaid of the user whose chat history is being viewed.
     */
    kaid: string;
    /**
     * URL path to the user's profile, needed for conversation links.
     */
    profilePath?: string;
    /**
     * The relationship between the viewer and the profile owner. Used for
     * analytics purposes.
     */
    relationship: Relationship;
    /**
     * Show the new table view instead of the conversations view.
     */
    renderTableView?: boolean;
    /**
     * For the conversations view, if a conversation is flagged, render the
     * notice in the first column, under the Chat Name.
     */
    showFlaggedNotice?: boolean;
    /**
     * The columns to display in the table. Defaults to all columns.
     * The values are the keys of the ThreadType object. Order matters.
     */
    visibleColumns?: Array<ColumnName>;
    /**
     * Whether the chat retention notice should be placed vertically or horizontally. Defaults to horizontal.
     */
    chatRetentionPosition?: "vertical" | "horizontal";
    /**
     * The name of the config to use for the query.
     */
    configName?: string;
}

type Props = OwnProps & WithMarkConversionProps;

/**
 * Gets the correct ISO datetime strings for the given date range.
 *
 * For start dates, we modify the time to the start of the day, while for end
 * dates, we modify the time to the very end of the day before converting to
 * ISO format. Returns null values if there is not a valid date or date range
 * provided.
 */
const dateRangeToISOStrings = (
    selection: DateRangeSelection,
): {startDate: string | null; endDate: string | null} => {
    const dateRange = selection.dateRange;
    const startDate = dateRange?.startDate
        ? moment(dateRange.startDate).startOf("day").toISOString()
        : null;
    const endDate = dateRange?.endDate
        ? moment(dateRange.endDate).endOf("day").toISOString()
        : null;
    return {startDate, endDate};
};

/**
 * Custom hook to manage date range state and sync with URL parameters.
 */
const useDateRangeState = (defaultDateRangeValue: DateRangeValue | null) => {
    const {
        queryParams: {
            startDate: queryParamsStartDate,
            endDate: queryParamsEndDate,
        },
    } = useLocation();

    const [dateRangeSelection, setDateRangeSelection] =
        useState<DateRangeSelection>(
            getSelectedDateRange(null, defaultDateRangeValue),
        );

    useEffect(() => {
        if (queryParamsStartDate && queryParamsEndDate) {
            setDateRangeSelection({
                selectedValue: "custom",
                dateRange: {
                    startDate: moment(queryParamsStartDate).toDate(),
                    endDate: moment(queryParamsEndDate).toDate(),
                },
            });
        }
    }, [queryParamsStartDate, queryParamsEndDate]);

    return {dateRangeSelection, setDateRangeSelection};
};

/**
 * Custom hook to manage flagged threads filter state.
 */
const useFlaggedThreadsFilter = () => {
    const {
        queryParams: {selectFlaggedChats},
    } = useLocation();

    const [flaggedThreadsOnly, setFlaggedThreadsOnly] =
        useState<boolean>(!!selectFlaggedChats);

    return {flaggedThreadsOnly, setFlaggedThreadsOnly};
};

/**
 * Custom hook to determine filter visibility logic.
 */
const useFilterVisibility = (
    relationship: Relationship,
    onClassroomExperience: boolean,
    pathname: string,
    dateRangeSelection: DateRangeSelection,
    flaggedThreadsOnly: boolean,
) => {
    const mayHaveTutorMeChats = useMemo(
        () => dateRangeSelection?.selectedValue === "*" || flaggedThreadsOnly,
        [dateRangeSelection?.selectedValue, flaggedThreadsOnly],
    );

    const isTutorMePage = useMemo(
        () => pathname.includes(TUTOR_ME_PATH),
        [pathname],
    );

    const shouldShowFilters = useMemo(
        () =>
            relationship === "COACH" ||
            relationship === "DISTRICT_ADMIN" ||
            onClassroomExperience,
        [relationship, onClassroomExperience],
    );

    return {
        mayHaveTutorMeChats,
        isTutorMePage,
        shouldShowFilters,
    };
};

/**
 * Custom hook to handle analytics events.
 */
const useAnalyticsEvents = (relationship: Relationship) => {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    useEffect(() => {
        publishAnalyticsEvent({
            eventType: "AiGuideHistoryOpened",
            eventSchemaVersion: 3,
            reviewerRelationship: relationship,
        });

        return function cleanup() {
            publishAnalyticsEvent({
                eventType: "AiGuideHistoryClosed",
                eventSchemaVersion: 3,
                reviewerRelationship: relationship,
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want this to run once on mount.
    }, []);
};

/**
 * Renders a view of AI guide transcripts (chat history) with various filtering
 * options. NOTE: this is used in both Classic Khan and Classroom.
 */
const GuideTranscripts = ({
    kaid,
    markConversion,
    profilePath,
    relationship,
    renderTableView,
    showFlaggedNotice,
    visibleColumns,
    chatRetentionPosition = "horizontal",
    configName,
}: Props): React.ReactElement => {
    const {entrypoint} = useContext(AppShellContext);
    const onClassroomExperience = entrypoint === CLASSROOM_ENTRYPOINT;

    // Determine default date range based on experience
    const defaultDateRangeValue = onClassroomExperience
        ? null // null === "All time"/ no date range selected
        : DEFAULT_DATE_RANGE_VALUE;

    // Custom hooks
    const {dateRangeSelection, setDateRangeSelection} = useDateRangeState(
        defaultDateRangeValue,
    );
    const {flaggedThreadsOnly, setFlaggedThreadsOnly} =
        useFlaggedThreadsFilter();

    const {pathname} = useLocation();
    const {mayHaveTutorMeChats, isTutorMePage, shouldShowFilters} =
        useFilterVisibility(
            relationship,
            onClassroomExperience,
            pathname,
            dateRangeSelection,
            flaggedThreadsOnly,
        );

    // Pagination state management (like learner reports)
    const [allFetchedThreads, setAllFetchedThreads] = useState<
        Array<AIGuideThreads>
    >([]);
    const [currentCursor, setCurrentCursor] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(0);

    // Analytics and page setup
    useOnMountEffect(() => {
        markConversion({id: "guide_transcripts_page_view"});
    });
    useAnalyticsEvents(relationship);

    // Query data
    const {startDate, endDate} = dateRangeToISOStrings(dateRangeSelection);

    const [result] = useQuery(GUIDE_TRANSCRIPTS_QUERY, {
        variables: {
            kaid,
            cursor: currentCursor,
            startDate,
            endDate,
            moderationStatus: flaggedThreadsOnly ? "FLAGGED" : "ALL",
            limit: GUIDE_TRANSCRIPTS_QUERY_PAGE_SIZE,
            configName,
        },
        fetchPolicy: FetchPolicy.NetworkOnly,
    });

    // Extract memoized current query results
    const resultThreads = useMemo((): Array<AIGuideThreads> => {
        if (result.status === "success") {
            const data = result.data;
            return data?.aiGuideThreads?.threads || [];
        }
        return [];
    }, [result]);

    // Extract memoized cursor for next query
    const resultCursor = useMemo((): string | null => {
        if (result.status === "success") {
            const data = result.data;
            return data?.aiGuideThreads?.cursor || null;
        }
        return null;
    }, [result]);

    // Builds the fetched threads (so we can manage what is already fetched)
    const appendToAllFetchedThreads = (threads: Array<AIGuideThreads>) => {
        setAllFetchedThreads((prevThreads) => [...prevThreads, ...threads]);
    };

    const resetThreadsData = () => {
        setAllFetchedThreads([]);
        setCurrentCursor(null);
        setCurrentPage(0);
    };

    // Set up after initial fetch of threads
    if (allFetchedThreads.length === 0 && resultThreads.length > 0) {
        appendToAllFetchedThreads(resultThreads);
        setCurrentCursor(resultCursor);
        setCurrentPage(1);
    }

    // Reset threads data when filters change
    useEffect(() => {
        resetThreadsData();
    }, [dateRangeSelection, flaggedThreadsOnly, kaid, configName]);

    const handlePageChange = (nextPage: number) => {
        const lastPage = Math.ceil(allFetchedThreads.length / TABLE_PAGE_SIZE);
        setCurrentPage(nextPage);

        /* If we're about to go to the last page and there is a result cursor,
         * we need to fetch more data by updating cursor and refetching.
         * The refetch will happen automatically due to the useQuery
         * dependency on currentCursor.
         */
        if (nextPage === lastPage && resultCursor) {
            setCurrentCursor(resultCursor);
        }
    };

    // Handle appending new data when we get results after cursor update
    useEffect(() => {
        // Only append new data if:
        // 1. We have a successful result
        // 2. We have both current and fetched threads
        // 3. The current threads are not already included in fetched threads
        if (
            result.status === "success" &&
            resultThreads.length > 0 &&
            allFetchedThreads.length > 0 &&
            currentCursor !== null
        ) {
            // Check if this is new data by comparing the first thread ID
            const firstCurrentThreadId = resultThreads[0]?.id;
            const isNewData = !allFetchedThreads.some(
                (thread) => thread.id === firstCurrentThreadId,
            );

            if (isNewData) {
                appendToAllFetchedThreads(resultThreads);
            }
        }
    }, [result.status, resultThreads, allFetchedThreads, currentCursor]);

    // Determine if we should render the history section at all
    const isDefaultFilters =
        dateRangeSelection?.selectedValue === "*" && !flaggedThreadsOnly;
    const shouldHideHistorySection =
        isTutorMePage && allFetchedThreads.length === 0 && isDefaultFilters;

    if (shouldHideHistorySection) {
        return <></>;
    }

    /**
     * Determines whether to render the loaded content based on page type.
     */
    const shouldRenderContent = (): boolean => {
        if (isTutorMePage) {
            // For tutor-me pages, show content if we have threads or if we're using default filters
            return allFetchedThreads.length > 0 || mayHaveTutorMeChats;
        }
        return true;
    };

    return (
        <View>
            <Helmet>
                <title>{t`Khanmigo Chat History`}</title>
            </Helmet>
            {!renderTableView && (
                <ConversationsViewHeading
                    profilePath={profilePath}
                    dateRangeSelection={dateRangeSelection}
                    setDateRangeSelection={setDateRangeSelection}
                    flaggedThreadsOnly={flaggedThreadsOnly}
                    setFlaggedThreadsOnly={setFlaggedThreadsOnly}
                />
            )}
            {renderTableView && shouldShowFilters && (
                <GuideTranscriptsFilters
                    dateRangeSelection={dateRangeSelection}
                    setDateRangeSelection={setDateRangeSelection}
                    flaggedThreadsOnly={flaggedThreadsOnly}
                    setFlaggedThreadsOnly={setFlaggedThreadsOnly}
                    userIsLearner={relationship === "SELF"}
                    chatRetentionPosition={chatRetentionPosition}
                />
            )}
            <View
                style={
                    // If the chat retention notice is placed vertically, we
                    // need to add more spacing between the heading and content
                    chatRetentionPosition === "vertical"
                        ? styles.largeSpacing
                        : styles.smallSpacing
                }
            />
            {shouldRenderContent() && (
                <GuideTranscriptsContent
                    result={result}
                    threads={allFetchedThreads}
                    renderTableView={renderTableView}
                    showFlaggedNotice={showFlaggedNotice}
                    visibleColumns={visibleColumns}
                    relationship={relationship}
                    profilePath={profilePath}
                    hasAppliedFilters={!isDefaultFilters}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    hasMorePages={resultCursor !== null}
                    isTutorMePage={isTutorMePage}
                />
            )}
            <View style={styles.mediumSpacing} />
        </View>
    );
};

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof GuideTranscripts,
        React.ComponentProps<typeof GuideTranscripts>
    >
>;

const GuideTranscriptsWithComposed = compose(
    withConversionContext({
        activity: enums.Activity.OTHER_ACTIVITY,
    }),
    withMarkConversion,
)(GuideTranscripts) as React.ComponentType<ExportProps>;
export {GuideTranscriptsWithComposed as GuideTranscripts};

const styles = StyleSheet.create({
    largeSpacing: {
        blockSize: sizing.size_280,
    },
    smallSpacing: {
        blockSize: sizing.size_160,
    },
    mediumSpacing: {
        height: sizing.size_100,
    },
});
